import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaCreditCard, FaSearch, FaTimes, FaCheck, FaBan } from 'react-icons/fa';
import {
  getAdminSubscriptions, cancelAdminSubscription, getPlans,
} from '../../../api/services/adminService';
import {
  SectionHeader, Badge, EmptyState, LoadingSkeleton, Alert, Pagination, Modal, ConfirmDialog,
} from './AdminUI';
import './Subscriptions.css';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const statusBadge = (status, expired) => {
  const s = String(status || '').toUpperCase();
  if (expired || s === 'EXPIRED' || s === 'CANCELLED' || s === 'CANCELED') return <Badge status="neutral">Expired</Badge>;
  if (s === 'ACTIVE') return <Badge status="success">Active</Badge>;
  if (s === 'PENDING') return <Badge status="warning">Pending</Badge>;
  return <Badge status="info">{status || '—'}</Badge>;
};

const Subscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminSubscriptions({ page, size });
      const data = res?.data || res;
      setSubs(data?.content || []);
      setTotalPages(data?.totalPages || 0);
      setTotalElements(data?.totalElements || 0);
    } catch (err) {
      setError(err?.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  useEffect(() => {
    fetchSubs();
  }, [fetchSubs]);

  const filtered = useMemo(() => {
    let list = subs;
    if (statusFilter) {
      list = list.filter((s) => String(s.status || '').toUpperCase() === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (s) =>
          s.userEmail?.toLowerCase().includes(q) ||
          s.planTitle?.toLowerCase().includes(q) ||
          s.paymentReference?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [subs, statusFilter, searchQuery]);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelAdminSubscription(cancelTarget.id);
      setActionMsg({ type: 'success', text: 'Subscription cancelled successfully.' });
      setCancelTarget(null);
      fetchSubs();
    } catch (err) {
      setActionMsg({ type: 'error', text: err?.message || 'Failed to cancel subscription' });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div>
      <SectionHeader
        title="Subscriptions"
        subtitle="View and manage user subscription plans across the platform."
        action={
          <button type="button" className="admin-btn admin-btn-outline" onClick={fetchSubs} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      {actionMsg && (
        <Alert type={actionMsg.type} onClose={() => setActionMsg(null)}>{actionMsg.text}</Alert>
      )}
      {error && <Alert type="error">{error} — <button type="button" onClick={fetchSubs} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>retry</button></Alert>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-subtle)', fontSize: 13 }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email, plan, or payment ref…"
            style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 8, border: '1px solid var(--admin-border-strong)', fontFamily: 'var(--admin-font)', fontSize: 13.5, color: 'var(--admin-text)', background: 'var(--admin-surface)' }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--admin-border-strong)', fontFamily: 'var(--admin-font)', fontSize: 13.5, color: 'var(--admin-text)', background: 'var(--admin-surface)', cursor: 'pointer' }}
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PENDING">Pending</option>
          <option value="EXPIRED">Expired</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="admin-table-wrap"><LoadingSkeleton lines={8} /></div>
      ) : filtered.length === 0 ? (
        <div className="admin-table-wrap">
          <EmptyState icon="💳" title="No subscriptions found" text={searchQuery || statusFilter ? 'Try adjusting your filters.' : 'No subscriptions have been created yet.'} />
        </div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Billing Cycle</th>
                  <th>Status</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Payment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.userEmail || '—'}</td>
                    <td>{s.planTitle || '—'}</td>
                    <td style={{ color: 'var(--admin-text-muted)' }}>{s.billingCycle || '—'}</td>
                    <td>{statusBadge(s.status, s.expired)}</td>
                    <td style={{ color: 'var(--admin-text-muted)' }}>{formatDate(s.startDate)}</td>
                    <td style={{ color: 'var(--admin-text-muted)' }}>{formatDate(s.endDate)}</td>
                    <td style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>
                      {s.paymentMethod ? `${s.paymentMethod}` : '—'}
                      {s.paymentReference ? <br /> : null}
                      {s.paymentReference || ''}
                    </td>
                    <td>
                      {String(s.status).toUpperCase() === 'ACTIVE' && !s.expired ? (
                        <button type="button" className="admin-btn admin-btn-ghost" style={{ color: 'var(--admin-danger)' }} onClick={() => setCancelTarget(s)}>
                          <FaBan style={{ fontSize: 11 }} /> Cancel
                        </button>
                      ) : (
                        <span style={{ color: 'var(--admin-text-subtle)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} totalElements={totalElements} pageSize={size} onPageChange={setPage} />
        </>
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancel subscription?"
        message={cancelTarget ? `This will cancel the ${cancelTarget.planTitle} subscription for ${cancelTarget.userEmail}. The user will lose access at the end of the current period.` : ''}
        confirmLabel="Cancel subscription"
        danger
      />
    </div>
  );
};

export default Subscriptions;
